import { Space } from '@timeedit/ui-components/antd';
import { Typography } from '@timeedit/ui-components/components/Typography/Typography';
import {
  DATE_LIMIT_DEFAULT_END,
  DATE_LIMIT_DEFAULT_START,
} from '../../constants/DateLimits.constants';
import { useDefaultTimeZone } from '../../hooks/useDefaultTimeZone';
import intl from '../../i18n/intl';
import { IRangeValue } from '../../types/dateTime.type';
import { DateLimit } from '../../types/hubStudentSettings.type';
import {
  dateToEndTimeUnix,
  dateToStartTimeUnix,
  endTimeUnixToDate,
  startTimeUnixToDate,
} from '../../utils/timeConverter';
import { validateDateLimit } from '../AppService/HubSettings/Student/ReservationSettings/validation';
import CheckableDateRange from '../CheckableDateRange/CheckableDateRange';
import RelativeTime from '../RelativeTime/RelativeTime';

const language = intl.messages as Record<string, string>;

type DateLimitsProps = {
  datelimit: DateLimit;
  setDatelimit: (value: DateLimit) => void;
};

export function DateLimits({ datelimit, setDatelimit }: DateLimitsProps) {
  const {
    timezone: defaultTimezone,
    loading: timezoneLoading,
    hasErrors: timezoneHasErrors,
    ready: timezoneReady,
  } = useDefaultTimeZone();

  const handleDateRangeChange = (range: IRangeValue) => {
    if (!timezoneReady) {
      return;
    }
    if (!defaultTimezone) {
      return;
    }
    setDatelimit({
      ...datelimit,
      absoluteStartTime: dateToStartTimeUnix(range?.[0], defaultTimezone),
      absoluteEndTime: dateToEndTimeUnix(range?.[1], defaultTimezone),
    });
  };

  const validation = validateDateLimit(datelimit ?? {});
  const issues = validation.success ? [] : validation.error.issues;
  const dateRangeStatus = issues.some(
    (issue) => issue.path[0] === 'absoluteEndTime'
  )
    ? 'error'
    : undefined;
  const offsetStatus = issues.some(
    (issue) => issue.path[0] === 'relativeEndTime'
  )
    ? 'error'
    : undefined;

  const dateRange: IRangeValue = timezoneReady
    ? [
        startTimeUnixToDate(datelimit?.absoluteStartTime, defaultTimezone),
        endTimeUnixToDate(datelimit?.absoluteEndTime, defaultTimezone),
      ]
    : [null, null];

  return (
    <div className="row-box">
      <Typography.Title level={5}>{language.date_limits}</Typography.Title>
      <Space orientation="vertical">
        <CheckableDateRange
          label={language.defined_date_range}
          tooltip={language.defined_date_range_help}
          value={dateRange}
          onChange={handleDateRangeChange}
          allowEmpty
          status={dateRangeStatus}
          loading={timezoneLoading}
          disabled={timezoneHasErrors || !timezoneReady}
        />
        <RelativeTime
          label={language.offset_start}
          tooltip={language.offset_start_help}
          daysOnly
          value={datelimit?.relativeStartTime ?? DATE_LIMIT_DEFAULT_START}
          onChange={(value) =>
            setDatelimit({ ...datelimit, relativeStartTime: value })
          }
          dataTestId="date-limits-start"
          status={offsetStatus}
        />
        <RelativeTime
          label={language.offset_end}
          tooltip={language.offset_end_help}
          daysOnly
          value={datelimit?.relativeEndTime ?? DATE_LIMIT_DEFAULT_END}
          onChange={(value) =>
            setDatelimit({ ...datelimit, relativeEndTime: value })
          }
          dataTestId="date-limits-end"
          status={offsetStatus}
        />
      </Space>
    </div>
  );
}
